import type { UpdateTemplateRequestBody } from "@/common/types/templates";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";

import { Layout } from "@/components/layout";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { toast } from "@/hooks/use-toast";
import { useUpdateTemplate } from "@/hooks/templates/useUpdateTemplate";
import { container } from "@/lib/utils";
import { useNavigate, useParams } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTemplateById } from "@/hooks/templates/useTemplateById";
import { useAuthStore } from "@/store/auth.store";

export const TemplateEdit = (): JSX.Element => {
  const { t } = useTranslation();
  const { userData } = useAuthStore();
  const { templateId }: { templateId: string } = useParams({ strict: false });
  const { updateTemplate, error, result, loading } = useUpdateTemplate();
  const { result: template } = useTemplateById(templateId);
  const navigate = useNavigate();

  const handleSuccess = (data: TemplateFormSchemaType): void => {
    const body: UpdateTemplateRequestBody = {
      userId: userData!.id,
      id: Number(templateId),
      name: data.name,
      headers: data.headers,
      isDefault: data.isDefault,
      optionalText: data.optionalText ?? "",
    };

    void updateTemplate(body);
  };

  useEffect(() => {
    const wasSuccessful = result == true && !loading;
    const wasError = error && result != null && !loading;

    if (wasSuccessful) {
      void navigate({ to: "/templates" });
      toast({
        variant: "default",
        title: t("templates.updatedToast.successful.title"),
        description: t("templates.updatedToast.successful.description"),
      });
    }

    if (wasError) {
      toast({
        variant: "error",
        title: t("templates.updatedToast.error.title"),
        description: t("templates.updatedToast.error.description"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading, error]);

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="min-h-screen bg-background p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {template && (
            <TemplateForm
              submitText={t("templates.editTemplate.submit")}
              template={template}
              onCancel={() => navigate({ to: "/templates" })}
              onSubmit={handleSuccess}
            />
          )}
        </motion.div>
      </div>
    </Layout>
  );
};
